/**
 * AirplaneMovementComponent - arcade-style flight controls. Translates pitch,
 * yaw, roll inputs into rotation with forward throttle and auto-leveling.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { MathHelpers } from '../../utils/MathHelpers.js';
import { property } from '../../utils/serialization/decorator.js';

import { BasePawnMovementComponent } from './BasePawnMovementComponent.js';

import type { SceneComponentOptions } from '../SceneComponent.js';

export interface AirplaneRotationParams {
  /** Maximum rotation in degrees per second */
  maxSpeedDPS: number;
  /** Acceleration when input is not zero */
  accelerationLambda: number;
  /** Deceleration when input is zero */
  decelerationLambda: number;
  /** Maximum degrees of rotation */
  maxDegrees?: number;
  /** Speed at which the rotation auto-levels to zero when the current rotation speed is zero */
  autoLevelLambda?: number;
}

/**
 * Configuration options for airplane movement
 */
export interface AirplaneMovementOptions extends SceneComponentOptions {
  /** Pitch rotation parameters */
  pitchParams: AirplaneRotationParams;
  /** Yaw rotation parameters */
  yawParams: AirplaneRotationParams;
  /** Roll rotation parameters */
  rollParams: AirplaneRotationParams;
  /** Throttle change rate (how quickly throttle changes) */
  throttleRate: number;
  /** Maximum forward speed */
  maxSpeed: number;
  /** Minimum forward speed (can be 0 or higher) */
  minSpeed: number;
  /** Initial throttle value (0-1) */
  initialThrottle?: number;
}

@EngineClass('AirplaneMovementComponent')
export class AirplaneMovementComponent extends BasePawnMovementComponent {


  @property()
  public pitchParams: AirplaneRotationParams = {
      maxSpeedDPS: 30,
      accelerationLambda: 2,
      decelerationLambda: 100,
      autoLevelLambda: 0.5,
    };

  @property()
  public yawParams: AirplaneRotationParams = {
      maxSpeedDPS: 50,
      accelerationLambda: 3,
      decelerationLambda: 100,
    };

  @property()
  public rollParams: AirplaneRotationParams = {
      maxSpeedDPS: 30,
      accelerationLambda: 2,
      decelerationLambda: 100,
      maxDegrees: 60,
      autoLevelLambda: 3,
    };

  @property()
  public throttleRate: number = 0.5;

  @property()
  public maxSpeed: number = 50;

  @property()
  public minSpeed: number = 10;

  // Current state
  private currentSpeed: number = 0;
  private throttle: number = 0;
  private currentPitchSpeedDPS: number = 0;
  private currentYawSpeedDPS: number = 0;
  private currentRollSpeedDPS: number = 0;

  public override initialize(options?: AirplaneMovementOptions): void {
    super.initialize(options);
    this.pitchParams = options?.pitchParams ?? this.pitchParams;
    this.yawParams = options?.yawParams ?? this.yawParams;
    this.rollParams = options?.rollParams ?? this.rollParams;
    this.throttleRate = options?.throttleRate ?? this.throttleRate;
    this.maxSpeed = options?.maxSpeed ?? this.maxSpeed;
    this.minSpeed = options?.minSpeed ?? this.minSpeed;
    this.throttle = options?.initialThrottle ?? 0;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    const owner = this.getActor();
    if (!owner?.getPlayerController()) {
      // the airplane should not move on its own without a player controller
      return;
    }

    const rootComponent = owner.rootComponent;

    // input mapping
    const throttleInput = this.zoomInput.value;
    const pitchInput = -this.forwardInput.value;
    const yawInput = this.rightInput.value;
    const rollInput = this.rightInput.value;

    // Update throttle based on input
    this.throttle += throttleInput * this.throttleRate * deltaTime;
    this.throttle = THREE.MathUtils.clamp(this.throttle, 0, 1);

    // Calculate current speed based on throttle
    const minSpeed = this.minSpeed * this.speedModifier;
    const maxSpeed = this.maxSpeed * this.speedModifier;
    this.currentSpeed = THREE.MathUtils.lerp(minSpeed, maxSpeed, this.throttle);

    const worldQuaternion = new THREE.Quaternion();
    rootComponent.getWorldQuaternion(worldQuaternion);
    const worldRotation = new THREE.Euler().setFromQuaternion(worldQuaternion, 'YXZ');
    const localRotation = new THREE.Euler().setFromQuaternion(rootComponent.quaternion, 'YXZ');

    // pitch
    {
      const params = this.pitchParams;
      const currentRotation = localRotation.x;
      this.currentPitchSpeedDPS = this.updateRotationSpeed(pitchInput, this.currentPitchSpeedDPS, params, deltaTime);
      const hasInput = Math.abs(this.currentPitchSpeedDPS) > 0.1;
      if (hasInput) {
        const deltaDegrees = this.clampDelta(currentRotation * THREE.MathUtils.RAD2DEG, this.currentPitchSpeedDPS * deltaTime, params.maxDegrees);
        rootComponent.addLocalRotation(MathHelpers.makeRotationDegrees({ pitch: deltaDegrees }));
      }

      // include auto-leveling if there is no input or the input direction is opposite to the current rotation
      if (params.autoLevelLambda && (!hasInput || (this.currentPitchSpeedDPS > 0) !== (currentRotation > 0))) {
        const targetRotation = THREE.MathUtils.damp(currentRotation, 0, params.autoLevelLambda, deltaTime);
        rootComponent.addLocalRotation(MathHelpers.makeRotation({ pitch: targetRotation - currentRotation }));
      }
    }

    // yaw
    {
      const params = this.yawParams;
      const currentRotation = -worldRotation.y;
      this.currentYawSpeedDPS = this.updateRotationSpeed(yawInput, this.currentYawSpeedDPS, params, deltaTime);
      const hasInput = Math.abs(this.currentYawSpeedDPS) > 0.1;
      if (hasInput) {
        const deltaDegrees = this.clampDelta(currentRotation * THREE.MathUtils.RAD2DEG, this.currentYawSpeedDPS * deltaTime, params.maxDegrees);
        rootComponent.addWorldRotation(MathHelpers.makeRotationDegrees({ yaw: deltaDegrees }));
      }

      // include auto-leveling if there is no input or the input direction is opposite to the current rotation
      if (params.autoLevelLambda && (!hasInput || (this.currentYawSpeedDPS > 0) !== (currentRotation > 0))) {
        const targetRotation = THREE.MathUtils.damp(currentRotation, 0, params.autoLevelLambda, deltaTime);
        rootComponent.addWorldRotation(MathHelpers.makeRotation({ yaw: targetRotation - currentRotation }));
      }
    }

    // roll
    {
      const params = this.rollParams;
      const currentRotation = -localRotation.z;
      this.currentRollSpeedDPS = this.updateRotationSpeed(rollInput, this.currentRollSpeedDPS, params, deltaTime);
      const hasInput = Math.abs(this.currentRollSpeedDPS) > 0.1;
      if (hasInput) {
        const deltaDegrees = this.clampDelta(currentRotation * THREE.MathUtils.RAD2DEG, this.currentRollSpeedDPS * deltaTime, params.maxDegrees);
        rootComponent.addLocalRotation(MathHelpers.makeRotationDegrees({ roll: deltaDegrees }));
      }

      // include auto-leveling if there is no input or the input direction is opposite to the current rotation
      if (params.autoLevelLambda && (!hasInput || (this.currentRollSpeedDPS > 0) !== (currentRotation > 0))) {
        const targetRotation = THREE.MathUtils.damp(currentRotation, 0, params.autoLevelLambda, deltaTime);
        rootComponent.addLocalRotation(MathHelpers.makeRotation({ roll: targetRotation - currentRotation }));
      }
    }

    const forwardMovement = owner.forwardDirection().multiplyScalar(this.currentSpeed * deltaTime);

    rootComponent.addWorldPosition(forwardMovement);
  }

  //====================================================================================================

  private updateRotationSpeed(input: number, currentSpeed: number, params: AirplaneRotationParams, deltaTime: number): number {
    if (input) {
      const target = input > 0 ? params.maxSpeedDPS : -params.maxSpeedDPS;
      currentSpeed = THREE.MathUtils.damp(currentSpeed, target, params.accelerationLambda, deltaTime);
    }
    else {
      currentSpeed = THREE.MathUtils.damp(currentSpeed, 0, params.decelerationLambda, deltaTime);
    }
    return currentSpeed;
  }

  private clampDelta(current: number, delta: number, limitABS?: number): number {
    if (limitABS) {
      return THREE.MathUtils.clamp(current + delta, -limitABS, limitABS) - current;
    } else {
      return delta;
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Airplane';
  }

}
